/**
 * Component: Datalayers
 * ------------------------------------------------------------------------------
 * Datalayers functions and general helpers
 *
 * @namespace datalayers
 */
import Cookies from 'js-cookie';
import {on, capitalize, handleize} from '~/helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  featuredCollection: '[js-datalayer="featured_collection"]',
  limeSpotContainer: '.limespot-recommendation-box',
  productCard: '[js-product-card]',
  limeSpotProductCard: '[js-product-card-limespot]',
  footerLink: '[js-footer-link]',
  footerSocial: '[js-footer-social]',
  plpFilter: '[js-filters-container]',
  plpSort: '.bc-sf-filter-custom-sorting',
  promoContainer: '[js-datalayer-promo="container"]',
  promoName: '[js-datalayer-promo="name"]',
  promoCta: '[js-datalayer-promo="cta"]',
  designerContainer: '[js-datalayer-designer="container"]',
  designerCta: '[js-datalayer-designer="cta"]',
};

/**
 * Datalayer object.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    featuredCollection: [...document.querySelectorAll(selectors.featuredCollection)],
    limeSpotContainer: [...document.querySelectorAll(selectors.limeSpotContainer)],
    productCard: [...document.querySelectorAll(selectors.productCard)],
    footerLink: [...document.querySelectorAll(selectors.footerLink)],
    footerSocial: [...document.querySelectorAll(selectors.footerSocial)],
    plpFilter: [...document.querySelectorAll(selectors.plpFilter)],
    plpSort: [...document.querySelectorAll(selectors.plpSort)],
    promoContainer: [...document.querySelectorAll(selectors.promoContainer)],
    designerCta: [...document.querySelectorAll(selectors.designerCta)],
  };

  /**
   * Initialise component.
   */
  function init() {
    pushSetupValue();

    if (nodeSelectors.featuredCollection.length > 0) {
      slidersPush();
    }

    if (nodeSelectors.productCard.length > 0 &&
      theme.pageType !== 'search' &&
      theme.pageType !== 'collection') {
      productCardsClick();
    }

    if (theme.pageType === 'search' || theme.pageType === 'collection') {
      filterClick();
      sortClick();
    }

    pushPromoBoxes();
    setEventListeners();
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    Frame.EventBus.listen('Datalayer:push', (response) => {
      datalayerPush(response);
    });

    Frame.EventBus.listen('Datalayer:limespotContainers', (response) => {
      limeSpotSliderPush(response);
    });

    Frame.EventBus.listen('Datalayer:limespotLinksReady', () => {
      limespotProductCardsClick();
    });

    Frame.EventBus.listen('Datalayer:productListReady', () => {
      productCardsClick();
    });

    nodeSelectors.designerCta.forEach((element) => {
      const container = element.closest(selectors.designerContainer);

      on('click', element, () => {
        window.dataLayer.push({
          event: 'GAevent',
          eventID: '15',
          eventCategory: 'product',
          eventAction: 'meet_the_designer',
          eventLabel: handleize(container.id.replace('ProductDesigner-', '')).replace(/-/g, '_'),
        });
      });
    });

    nodeSelectors.footerLink.forEach((element) => {
      on('click', element, () => {
        window.dataLayer.push({
          event: 'GAevent',
          eventID: '03',
          eventCategory: 'footer',
          eventAction: element.dataset.datalayerParent,
          eventLabel: element.dataset.datalayerTitle,
        });
      });
    });

    nodeSelectors.footerSocial.forEach((element) => {
      on('click', element, () => {
        window.dataLayer.push({
          event: 'GAevent',
          eventID: '04',
          eventCategory: 'social',
          eventAction: element.dataset.datalayerSocial.toLowerCase(),
          eventLabel: 'click',
        });
      });
    });
  }

  function pushPromoBoxes() {
    const promoBoxes = [];

    nodeSelectors.promoContainer.forEach((promo, index) => {

      const promoTitle = handleize(promo.querySelector(selectors.promoName).innerText.trim()).replace(/-/g, '_');
      const promoId = promo.closest('[data-section-id]').dataset.sectionId;
      const promoPosition = Number(index + 1);
      let promoCreativity = '';

      const promoBox = {
        id: promoId,
        name: promoTitle,
        creative: promoCreativity,
        position: promoPosition,
      };

      promoBoxes.push(promoBox);

      promo.querySelectorAll(selectors.promoCta).forEach((btn, idx) => {
        if (idx === 0) {
          promoCreativity = handleize(btn.href.split('/').pop().trim()).replace(/-/g, '_');
        }

        on('click', btn, () => {
          window.dataLayer.push({
            event: 'promoClick',
            ecommerce: {
              promoClick: {
                promotions: [{
                  id: promoId,
                  name: promoTitle,
                  creative: promoCreativity,
                  position: promoPosition,
                }],
              },
            },
          });
        });
      });

      if (index + 1 === nodeSelectors.promoContainer.length) {
        window.dataLayer.push({
          event: 'promoView',
          ecommerce: {
            promoView: {
              promotions: promoBoxes,
            },
          },
        });
      }
    });
  }

  function limeSpotSliderPush(response) {
    window.dataLayer.push({
      event: 'productImpression',
      ecommerce: {
        currencyCode: window.theme.currency,
        impressions: response,
      },
    });
  }

  function slidersPush() {
    nodeSelectors.featuredCollection.forEach((collection) => {
      const arrayName = `datalayer${capitalize(collection.dataset.datalayer)}`;
      const impressionsArray = window[arrayName];

      window.dataLayer.push({
        event: 'productImpression',
        ecommerce: {
          currencyCode: window.theme.currency,
          impressions: impressionsArray,
        },
      });
    });
  }

  function productCardsClick() {
    on('click', (event) => {
      const closestLink = event.target.closest(selectors.productCard);

      if (!closestLink) {
        return;
      }

      const productArray = closestLink.dataset.datalayerId;
      const productList = closestLink.dataset.datalayerList;

      window.dataLayer.push({
        event: 'productClick',
        ecommerce: {
          currencyCode: window.theme.currency,
          click: {
            actionField: {
              list: productList,
            },
            products: [window[productArray]],
          },
        },
      });

      localStorage.setItem('datalayerList', productList);
    });
  }

  function limespotProductCardsClick() {
    on('click', (event) => {
      const closestLink = event.target.closest(selectors.limeSpotProductCard);

      if (!closestLink) {
        return;
      }

      const productArray = closestLink.dataset.datalayerId;

      window.dataLayer.push({
        event: 'productClick',
        ecommerce: {
          currencyCode: window.theme.currency,
          click: {
            actionField: {
              list: 'limespot',
            },
            products: [window[productArray]],
          },
        },
      });

      localStorage.setItem('datalayerList', 'limespot');
    });
  }

  function sortClick() {
    nodeSelectors.plpSort.forEach((sortList) => {
      on('click', sortList, (event) => {
        if (!event.target.dataset.sort) {
          return;
        }

        let elementEventLabel = '';

        switch (event.target.dataset.sort) {
          case 'best-selling':
            elementEventLabel = 'sorting_best_selling';
            break;
          case 'manual':
            elementEventLabel = 'sorting_featured';
            break;
          case 'price-ascending':
            elementEventLabel = 'sorting_increase_price';
            break;
          case 'price-descending':
            elementEventLabel = 'sorting_decrease_price';
            break;
          case 'title-ascending':
            elementEventLabel = 'sorting_a_z';
            break;
          case 'title-descending':
            elementEventLabel = 'sorting_z_a';
            break;
          case 'created-descending':
            elementEventLabel = 'sorting_newest_to_oldest';
            break;
          case 'created-ascending':
            elementEventLabel = 'sorting_oldest_to_newest';
            break;
        }

        window.dataLayer.push({
          event: 'GAevent',
          eventID: '11',
          eventCategory: 'filter',
          eventAction: 'sort_by',
          eventLabel: elementEventLabel,
        });
      });
    });
  }

  function filterClick() {
    nodeSelectors.plpFilter.forEach((filterList) => {
      on('click', filterList, (event) => {
        let activeFilter;

        if (
          event.target.closest('#bc-sf-filter-mobile-footer') ||
          event.target.classList.contains('bc-sf-filter-select-button')
        ) {
          if (event.target.closest('#bc-sf-filter-mobile-footer')) {
            activeFilter = filterList.querySelector('.bc-sf-filter-option-block-active');
          } else {
            activeFilter = event.target.closest('.bc-sf-filter-option-block');
          }

          window.dataLayer.push({
            event: 'GAevent',
            eventID: '12',
            eventCategory: 'filter',
            eventAction: 'product_filter',
            eventLabel: activeFilter.dataset.type,
          });
        }

        if (event.target.closest('.bc-sf-filter-selection-wrapper')) {
          const element = event.target.closest('a');
          let elementLabel = '';

          if (element.querySelector('.bc-sf-filter-option-label')) {
            elementLabel = handleize(element.querySelector('.bc-sf-filter-option-label').innerHTML.trim()).replace(/-/g, '_');
          } else {
            elementLabel = 'clear_all';
          }

          window.dataLayer.push({
            event: 'GAevent',
            eventID: '13',
            eventCategory: 'filter',
            eventAction: 'remove',
            eventLabel: elementLabel,
          });
        }
      });
    });
  }

  /**
   * Set the initial setting for cookies in wSetup event of datalayer.
   */
  function pushSetupValue() {
    window.dataLayer = window.dataLayer || [];

    const cookieValue = (Cookies.get('cookie-policy') === 'accepted') ? 1 : 0;


    /* eslint-disable-next-line */
  }


  /**
   * Pushing event to datalayer.
   * @param {Object} response - Object to push into datalayer.
   */
  function datalayerPush(response) {
    if (response?.eventAction) {
      response.eventAction = formatValue(response.eventAction);
    }

    if (response?.eventLabel) {
      response.eventLabel = formatValue(response.eventLabel);
    }

    window.dataLayer.push(response);
  }

  /**
   * Format property value to have as datalayer.
   * @param {String} property - Object property value
   */
  function formatValue(value) {
    return value.replace(/-/g, '_').replace(/\s/g, '_');
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    datalayerPush,
    init,
  });
};
